const cardSurface =
  'rounded-2xl border border-slate-200 bg-white p-6 shadow-md transition hover:border-gold/35 hover:shadow-lg sm:p-7';

function IconBox({ children, className = '' }) {
  return (
    <span
      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15 ring-1 ring-gold/35 ${className}`}
    >
      {children}
    </span>
  );
}

function EyeIcon() {
  return (
    <svg className="h-5 w-5 text-gold" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12s3.912-6.75 9.75-6.75 9.75 6.75 9.75 6.75-3.912 6.75-9.75 6.75S2.25 12 2.25 12z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg className="h-5 w-5 text-gold" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 16a4 4 0 100-8 4 4 0 000 8zM12 14a2 2 0 100-4 2 2 0 000 4z"
      />
    </svg>
  );
}

function RibbonIcon() {
  return (
    <svg className="h-4 w-4 text-gold" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 6L19 8.5M5 8.5L7.5 6M19 8.5v7.5a2 2 0 01-2 2h-2.5l-2.5 2.5-2.5-2.5H7a2 2 0 01-2-2V8.5M16.5 6h-9L5 8.5M12 3v3"
      />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="h-4 w-4 text-gold" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );
}

function PuzzleIcon() {
  return (
    <svg className="h-4 w-4 text-gold" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-2.496c.18-.18.35-.39.5-.59a4.5 4.5 0 00-4.5-4.5c-.2.15-.41.32-.59.5L8.83 11.42M11.42 15.17L8.83 11.42M8.83 11.42L3 5.59a2.652 2.652 0 015.878-2.496L8.83 11.42"
      />
    </svg>
  );
}

const missionItems = [
  {
    title: 'World-Class Consultancy',
    text: 'Deliver exceptional consultancy services that exceed client expectations through expertise, innovation, and dedication.',
    Icon: RibbonIcon,
  },
  {
    title: 'Compliance & Transparency',
    text: 'Ensure complete regulatory compliance and maintain absolute transparency in all our processes and communications.',
    Icon: ShieldIcon,
  },
  {
    title: 'Holistic Solutions',
    text: "Provide comprehensive, end-to-end solutions that address all aspects of our clients' needs under one roof.",
    Icon: PuzzleIcon,
  },
];

export default function VisionMission() {
  return (
    <section
      className="relative overflow-hidden border-t border-white/10 bg-gradient-to-b from-navy-900 to-navy-950 py-16 sm:py-24"
      aria-labelledby="vision-mission-heading"
    >
      <div
        className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-tr from-navy-950/90 via-navy-900/85 to-navy-950/75"
        aria-hidden
      />
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header — same pattern as About Preview / Who We Are */}
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">Our Purpose</p>
        <h2
          id="vision-mission-heading"
          className="mt-3 font-sans text-3xl font-bold tracking-tight text-white md:text-4xl"
        >
          Vision{' '}
          <span className="font-serif text-3xl font-semibold italic text-gold md:text-4xl">&amp; Mission</span>
        </h2>
        <div className="mt-4 h-px w-16 bg-gradient-to-r from-transparent via-gold/80 to-transparent" aria-hidden />

        <div className="mt-10 grid gap-6 lg:grid-cols-2 lg:gap-8">
          {/* Vision */}
          <div className={cardSurface}>
            <div className="flex items-center gap-3">
              <IconBox>
                <EyeIcon />
              </IconBox>
              <h3 className="font-serif text-lg font-semibold text-gold sm:text-xl">Our Vision</h3>
            </div>
            <p className="mt-5 text-justify text-base leading-relaxed text-navy/80">
              To become the{' '}
              <span className="font-semibold text-gold-dark">most trusted global gateway</span> for individuals and
              businesses seeking international opportunities.
            </p>
            <blockquote className="mt-6 rounded-r-lg border-l-4 border-gold bg-slate-50/80 py-3 pl-4 pr-2 text-sm leading-relaxed text-navy/75 sm:text-base">
              We envision a world where borderless opportunities are accessible to everyone, facilitated by
              transparent, efficient, and reliable consultancy services that empower our clients to achieve their global
              aspirations.
            </blockquote>
          </div>

          {/* Mission */}
          <div className={cardSurface}>
            <div className="flex items-center gap-3">
              <IconBox>
                <TargetIcon />
              </IconBox>
              <h3 className="font-serif text-lg font-semibold text-gold sm:text-xl">Our Mission</h3>
            </div>
            <ul className="mt-6 space-y-5">
              {missionItems.map(({ title, text, Icon }) => (
                <li key={title} className="flex gap-4">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 ring-1 ring-gold/30">
                    <Icon />
                  </span>
                  <div>
                    <p className="font-semibold text-navy">{title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-navy/65">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
